import { useCallback, useEffect, useRef, useState } from 'react';
import { getWebSocketUrl } from '../lib/ws';

const MAX_RETRIES = 5;
const INITIAL_RETRY_MS = 1000;
/** Server registers message handler only after Gemini connects (~1–2s). */
const SERVER_READY_DELAY_MS = 2500;

interface UseReconnectingWebSocketOptions {
  path: string;
  onMessage: (event: MessageEvent) => void;
}

export function useReconnectingWebSocket({ path, onMessage }: UseReconnectingWebSocketOptions) {
  const wsRef = useRef<WebSocket | null>(null);
  const retryCountRef = useRef(0);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const readyTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const messageQueueRef = useRef<string[]>([]);
  const onMessageRef = useRef(onMessage);
  const disposedRef = useRef(false);
  const shouldReconnectRef = useRef(true);
  const [isConnected, setIsConnected] = useState(false);
  const [isServerReady, setIsServerReady] = useState(false);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);

  onMessageRef.current = onMessage;

  const flushQueue = useCallback(() => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    while (messageQueueRef.current.length > 0) {
      ws.send(messageQueueRef.current.shift()!);
    }
  }, []);

  const scheduleReady = useCallback(() => {
    if (readyTimerRef.current) clearTimeout(readyTimerRef.current);
    setIsServerReady(false);
    readyTimerRef.current = setTimeout(() => {
      if (disposedRef.current) return;
      retryCountRef.current = 0;
      setIsServerReady(true);
      flushQueue();
    }, SERVER_READY_DELAY_MS);
  }, [flushQueue]);

  const handleIncomingMessage = useCallback((event: MessageEvent) => {
    try {
      const data = JSON.parse(event.data);
      if (data?.error) {
        shouldReconnectRef.current = false;
      }
    } catch {
      // ignore non-JSON frames
    }
    onMessageRef.current(event);
  }, []);

  const connect = useCallback(() => {
    if (disposedRef.current) return;

    if (wsRef.current) {
      wsRef.current.onclose = null;
      wsRef.current.onerror = null;
      wsRef.current.close();
      wsRef.current = null;
    }

    const ws = new WebSocket(getWebSocketUrl(path));
    wsRef.current = ws;

    ws.onopen = () => {
      if (disposedRef.current) return;
      setIsConnected(true);
      setIsReconnecting(false);
      setConnectionError(null);
      scheduleReady();
    };

    ws.onmessage = handleIncomingMessage;

    const handleDisconnect = () => {
      if (disposedRef.current) return;

      setIsConnected(false);
      setIsServerReady(false);
      if (readyTimerRef.current) clearTimeout(readyTimerRef.current);

      if (!shouldReconnectRef.current) {
        setIsReconnecting(false);
        setConnectionError('Conexión cerrada.');
        return;
      }

      if (retryCountRef.current < MAX_RETRIES) {
        const delay = INITIAL_RETRY_MS * 2 ** retryCountRef.current;
        retryCountRef.current += 1;
        setIsReconnecting(true);
        setConnectionError('Reconectando…');
        retryTimerRef.current = setTimeout(connect, delay);
      } else {
        setIsReconnecting(false);
        setConnectionError('No se pudo conectar. Recarga la página para intentar de nuevo.');
      }
    };

    ws.onclose = handleDisconnect;
    ws.onerror = () => {
      if (!disposedRef.current) {
        setConnectionError('Error de conexión WebSocket.');
      }
    };
  }, [path, scheduleReady, handleIncomingMessage]);

  useEffect(() => {
    disposedRef.current = false;
    shouldReconnectRef.current = true;
    retryCountRef.current = 0;
    connect();

    return () => {
      disposedRef.current = true;
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
      if (readyTimerRef.current) clearTimeout(readyTimerRef.current);
      const ws = wsRef.current;
      if (ws) {
        ws.onclose = null;
        ws.onerror = null;
        ws.close();
        wsRef.current = null;
      }
    };
  }, [connect]);

  const send = useCallback(
    (data: string) => {
      if (wsRef.current?.readyState === WebSocket.OPEN && isServerReady) {
        wsRef.current.send(data);
      } else {
        messageQueueRef.current.push(data);
      }
    },
    [isServerReady],
  );

  return {
    send,
    isConnected,
    isServerReady,
    isReconnecting,
    connectionError,
    wsRef,
  };
}
